import React, { useCallback, useEffect, useRef, useState } from 'react';
import BottomNavigationScreen from '../Navigation/Bottom';
import {
  CustomInput,
  FilterPopup,
  Toast as CustomToast,
} from '../../../Components';
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import moment from 'moment/moment';
import { toast } from 'material-react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginPageStyles } from '../../../styles';
import { destroyUser } from '../../../redux/features/User';
import {
  fetchOrdersHistory,
  setOrdersHistory,
} from '../../../redux/features/Practice';
import { setCurrentTab } from '../../../redux/features/Tab';
import {
  Box,
  CssBaseline,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { EmptyStatesForTab } from '../Tabs';
import { RECORDS_STATUS } from '../../../Constants';
import { useAuth, useLiveFeed } from '../../../hooks';

const LogisticsHistory = () => {
  const classes = loginPageStyles();
  const user = useSelector((state) => state.userReducer.user);
  const historyData = useSelector(
    (state) => state.practicesReducer.ordersHistory
  );
  const loading = useSelector((state) => state.practicesReducer.loading);
  const hasMore = useSelector((state) => state.practicesReducer.hasMore);
  const totalPages = useSelector((state) => state.practicesReducer.totalPages);
  const [hasRecieved, setHasRecieved] = useLiveFeed();
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [filterPopup, setFilterPopup] = useState(false);
  const [apply, setApply] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const observer = useRef();
  const lastElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            page < totalPages && setPage((prev) => prev + 1);
          }
        },
        {
          root: null,
          rootMargin: '-10% 0px', // set negative rootMargin to start intersection earlier
          threshold: 0.9, // set higher threshold to require more of the element to be visible
        }
      );
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const [queryParams, setQueryParams] = useState({
    userId: user?.id,
    order_type: [],
    attributeToSort: '',
    sortBy: 'asc',
    sortByDate: false,
    startDate: new Date(),
    endDate: new Date(),
  });

  const getQueryParams = () => {
    let data = {
      rider_id: user?.id,
      orders_history: true,
      search: searchValue,
      page: page,
    };
    if (queryParams.order_type.length !== 0) {
      queryParams.order_type.forEach((o) => {
        data = { ...data, [o.toLowerCase()]: true };
      });
    }
    if (queryParams.attributeToSort !== '') {
      data = {
        ...data,
        sort: queryParams.attributeToSort,
        direction: queryParams.sortBy.toLowerCase(),
      };
    }

    if (queryParams.sortByDate) {
      if (queryParams.endDate !== null) {
        data = {
          ...data,
          start_date: moment(queryParams.startDate).format('YYYY-MM-DD'),
          end_date: moment(queryParams.endDate).format('YYYY-MM-DD'),
        };
      }
    }

    return data;
  };

  // Calling our Auth Hook
  useAuth();

  useEffect(() => {
    dispatch(setCurrentTab('History'));
  }, []);

  // useEffect for resetting state when tab Index changes
  useEffect(() => {
    if (queryParams.attributeToSort === '') {
      dispatch(setOrdersHistory([]));
      setPage(1);
    }
  }, [searchValue, apply]);

  const sortPractices = (by, order) => {
    let temp = JSON.parse(JSON.stringify(historyData));
    by === 'name'
      ? temp.sort((a, b) => {
          var x = a.name.toUpperCase();
          var y = b.name.toUpperCase();
          return order === 'asc'
            ? x === y
              ? 0
              : x > y
              ? 1
              : -1
            : x === y
            ? 0
            : x > y
            ? -1
            : 1;
        })
      : temp.sort((a, b) => {
          return order === 'asc'
            ? a.order_count.eorders - b.order_count.eorders
            : b.order_count.eorders - a.order_count.eorders;
        });

    dispatch(setOrdersHistory([...temp]));
    setQueryParams((prev) => {
      return {
        ...prev,
        attributeToSort: '',
        sortBy: '',
      };
    });
  };

  useEffect(() => {
    let promise;
    let params = { ...getQueryParams() };

    if (params.sort !== undefined && params.sort !== '') {
      sortPractices(params.sort, params.direction);
    } else {
      if (queryParams.endDate !== null) {
        promise = dispatch(fetchOrdersHistory(params));
        promise.unwrap().catch((err) => {
          if (
            err?.error === 'You need to sign in or sign up before continuing.'
          ) {
            toast(
              <CustomToast
                severity="warning"
                message="Your session has been expired"
              />,
              { closeButton: false }
            );
            dispatch(destroyUser());
            navigate('/login');
          }
          if (err?.name === 'AbortError') return;
        });
      }
    }
    // Cleanup Function
    return () => {
      promise && promise.abort();
    };
  }, [searchValue, page, apply]);

  const searchFilter = async (e) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handleFilter = () => {
    setFilterPopup(true);
  };

  // This function handles new Order Alert
  const handleNewOrderAlert = () => {
    toast(
      <CustomToast
        severity="info"
        message="New Orders Assigned. Tap to view"
        handleClick={() => navigate('/logistics')}
      />,
      {
        closeButton: false,
        autoClose: false,
      }
    );
    setHasRecieved(false);
  };
  const buttonStyle = {
    fontFamily: 'Mulish',
    backgroundColor: '#F2F4F7',
    textTransform: 'capitalize',
    color: '#455066',
    fontSize: '13px',
    lineHeight: '16px',
    letterSpacing: '0.01em',
  };

  return (
    <React.Fragment>
      <CssBaseline />
      {hasRecieved && handleNewOrderAlert()}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          width: '100%',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box
            className={classes.heading10}
            sx={{
              color: '#0F2853',
              fontWeight: '700',
              fontSize: '21px',
              lineHeight: '27px',
              my: '4px',
              textAlign: 'center',
            }}
          >
            History
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              marginBottom: '12px',
              mx: '15px',
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search"
              value={searchValue}
              className={classes.textField}
              sx={{ pl: '8px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#8493AE' }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue.length > 0 && (
                  <InputAdornment position="end">
                    <IconButton
                      sx={{ p: 0 }}
                      onClick={() => setSearchValue('')}
                    >
                      <CloseIcon size="small" sx={{ color: '#8493AE' }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={searchFilter}
            />
            <Button
              variant="text"
              sx={{
                p: 0,
                ':hover': {
                  bgcolor: '#ffffff',
                  color: '#000000',
                },
              }}
              onClick={handleFilter}
            >
              <FilterListIcon
                sx={{
                  fontSize: '2rem',
                  color:
                    queryParams.order_type.length !== 0 ||
                    queryParams.attributeToSort !== '' ||
                    (queryParams.endDate !== null &&
                      queryParams.endDate.getDate() !==
                        queryParams.startDate.getDate())
                      ? '#054E8B'
                      : '#8493AE',
                }}
              />
            </Button>
          </Box>
          <Box
            className={classes.heading10}
            sx={{
              color: '#FAFAFA',
              bgcolor: '#054E8B',
              py: '2px',
              pl: '16px',
              fontWeight: '700',
              fontSize: '20px',
              lineHeight: '33px',
              width: '100%',
            }}
          >
            Dispatch History{' '}
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Displaying Practices here */}
          <Box sx={{ overflow: 'auto' }}>
            {!loading && historyData?.length === 0 && hasMore === false ? (
              <EmptyStatesForTab
                title={RECORDS_STATUS[4]?.title}
                description={RECORDS_STATUS[4]?.description}
              />
            ) : (
              historyData?.map((data, index) => {
                return index === historyData.length - 1 ? (
                  <Box key={index} ref={lastElementRef}>
                    <Box
                      sx={{
                        padding: '8px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderWidth: '1px 0px',
                        borderStyle: 'solid',
                        borderColor: '#CAD3E6',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Box
                          className={classes.heading10}
                          sx={{
                            fontSize: '17px',
                            fontWeight: '700',
                            lineHeight: '22px',
                            color: '#2A3752',
                          }}
                        >
                          {data.name}
                        </Box>
                      </Box>
                      <Button
                        variant="text"
                        sx={{
                          height: '20px',
                          borderRadius: '20px',
                          border: '2px solid #54CB83',
                          padding: '2px 21.5px',
                          backgroundColor: '#F2F4F7',
                        }}
                        style={buttonStyle}
                      >
                        Delivered
                      </Button>
                    </Box>
                    {data.orders_history?.map((order, idx) => (
                      <Box key={idx}>
                        <Box
                          sx={{
                            padding: '2px 16px',
                            bgcolor: '#EBEFF5',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #DAE0EB',
                            height: '36px',
                          }}
                        >
                          <Box
                            sx={{
                              fontFamily: 'Mulish',
                              color: '#2A3752',
                              fontSize: '15px',
                              lineHeight: '19px',
                              width: '100%',
                              mr: '3px',
                              display: 'flex',
                            }}
                          >
                            <strong> {idx + 1}</strong>.{' '}
                            <Box
                              sx={{ display: 'flex', lineBreak: 'anywhere' }}
                            >
                              {order.lab_barcode_id}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignContent: 'end',
                              alignItems: 'center',
                            }}
                          >
                            <CustomInput
                              total={
                                order.specimens
                                  ? order.specimens
                                  : order.samples_count
                              }
                              collected={
                                order.specimens
                                  ? order.specimens
                                  : order.samples_count
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box key={index}>
                    <Box
                      sx={{
                        padding: '8px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderWidth: '1px 0px',
                        borderStyle: 'solid',
                        borderColor: '#CAD3E6',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Box
                          className={classes.heading10}
                          sx={{
                            fontSize: '17px',
                            fontWeight: '700',
                            lineHeight: '22px',
                            color: '#2A3752',
                          }}
                        >
                          {data.name}
                        </Box>
                      </Box>
                      <Button
                        variant="text"
                        sx={{
                          height: '20px',
                          borderRadius: '20px',
                          border: '2px solid #54CB83',
                          padding: '2px 21.5px',
                          backgroundColor: '#F2F4F7',
                        }}
                        style={buttonStyle}
                      >
                        Delivered
                      </Button>
                    </Box>
                    {data.orders_history?.map((order, idx) => (
                      <Box key={idx}>
                        <Box
                          sx={{
                            padding: '2px 16px',
                            bgcolor: '#EBEFF5',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #DAE0EB',
                            height: '36px',
                          }}
                        >
                          <Box
                            sx={{
                              fontFamily: 'Mulish',
                              color: '#2A3752',
                              fontSize: '15px',
                              lineHeight: '19px',
                              width: '100%',
                              mr: '3px',
                              lineBreak: 'anywhere',
                              display: 'flex',
                            }}
                          >
                            <strong> {idx + 1}</strong>.{' '}
                            <Box
                              sx={{ display: 'flex', lineBreak: 'anywhere' }}
                            >
                              {order.lab_barcode_id}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignContent: 'end',
                              alignItems: 'center',
                            }}
                          >
                            <CustomInput
                              total={
                                order.specimens
                                  ? order.specimens
                                  : order.samples_count
                              }
                              collected={
                                order.specimens
                                  ? order.specimens
                                  : order.samples_count
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                );
              })
            )}
            {/* Displaying Loader Here */}
            {loading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  my: 'auto',
                  height: 'calc(100vh - 144px)',
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Box>
        {filterPopup && (
          <FilterPopup
            filterPopup={filterPopup}
            setFilterPopup={setFilterPopup}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            apply={apply}
            setApply={setApply}
          />
        )}
        <BottomNavigationScreen />
      </Box>
    </React.Fragment>
  );
};
export default LogisticsHistory;
